import { Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { NOTIFICATION_EVENT_CATALOG, NOTIFICATION_POLICY_SEED } from '@common/data';
import type { NotificationChannel, NotificationEventKey, NotificationPolicy } from '@common/types';

const SAVE_LATENCY_MS = 280;

@Injectable({ providedIn: 'root' })
export class NotificationPolicyService {
  readonly catalog = NOTIFICATION_EVENT_CATALOG;

  private readonly policies = signal<Record<string, NotificationPolicy>>({
    [NOTIFICATION_POLICY_SEED.scopeId]: NOTIFICATION_POLICY_SEED,
  });

  forScope(scopeId: string): NotificationPolicy {
    return this.policies()[scopeId] ?? { ...NOTIFICATION_POLICY_SEED, scopeId };
  }

  save(next: NotificationPolicy): Observable<NotificationPolicy> {
    this.policies.update((map) => ({ ...map, [next.scopeId]: next }));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  toggleChannel(
    scopeId: string,
    event: NotificationEventKey,
    channel: NotificationChannel,
  ): Observable<NotificationPolicy> {
    const current = this.forScope(scopeId);
    return this.save({
      ...current,
      rules: current.rules.map((rule) => {
        if (rule.event !== event) return rule;
        const channels = rule.channels.includes(channel)
          ? rule.channels.filter((entry) => entry !== channel)
          : [...rule.channels, channel];
        return { ...rule, channels };
      }),
    });
  }

  toggleRule(scopeId: string, event: NotificationEventKey): Observable<NotificationPolicy> {
    const current = this.forScope(scopeId);
    return this.save({
      ...current,
      rules: current.rules.map((rule) =>
        rule.event === event ? { ...rule, isEnabled: !rule.isEnabled } : rule,
      ),
    });
  }
}
